@if (product(); as product) {
  <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
    @if (isOwner()) {
      <div
        class="mb-6 overflow-hidden rounded-2xl border border-line bg-white shadow-[0_1px_2px_rgba(33,31,26,0.04)]"
      >
        <div class="border-l-[3px] border-l-tan px-4 py-4 sm:px-5">
          <!-- Mobile: message stacked above a full-width action row -->
          <div class="flex flex-col gap-3.5 sm:hidden">
            <div class="flex items-start gap-3">
              <span
                class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-creamdark text-tandark"
              >
                <app-icon name="eye" svgClass="h-4 w-4" [strokeWidth]="2" />
              </span>
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="font-display text-sm font-semibold text-ink">Seller preview</p>
                  <span
                    class="rounded-full px-2 py-0.5 text-[11px] font-medium"
                    [class]="productStatusBadgeClass(product.status)"
                  >
                    {{ productStatusLabel(product.status) }}
                  </span>
                </div>
                <p class="mt-0.5 text-xs leading-relaxed text-ink/55">
                  You’re viewing your listing as a buyer would — purchase actions stay disabled.
                </p>
              </div>
            </div>

            <div class="flex gap-2">
              <a
                routerLink="/account/listings"
                class="inline-flex flex-1 items-center justify-center gap-1 rounded-full border border-line bg-cream/80 px-3 py-2.5 text-xs font-medium text-ink/70 transition hover:border-tan hover:text-ink"
              >
                <app-icon name="chevronLeft" svgClass="h-3.5 w-3.5" />
                Listings
              </a>
              <a
                [routerLink]="['/account/listings', product.id, 'edit']"
                class="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-line bg-white px-3 py-2.5 text-xs font-medium text-ink transition hover:border-tan hover:bg-cream"
              >
                <app-icon name="pencil" svgClass="h-3.5 w-3.5" [strokeWidth]="2" />
                Edit
              </a>
              @if (product.status === 'DRAFT') {
                <button
                  type="button"
                  class="inline-flex flex-1 cursor-pointer items-center justify-center rounded-full bg-forest px-3 py-2.5 text-xs font-medium text-cream transition hover:bg-forestdark disabled:cursor-not-allowed disabled:opacity-60"
                  [disabled]="publishing()"
                  (click)="publishListing()"
                >
                  {{ publishing() ? 'Publishing…' : 'Publish' }}
                </button>
              }
            </div>
            @if (publishError()) {
              <p class="text-xs text-rose">{{ publishError() }}</p>
            }
          </div>

          <!-- Desktop: single balanced toolbar -->
          <div class="hidden sm:flex sm:items-center sm:justify-between sm:gap-6">
            <div class="flex min-w-0 items-center gap-3">
              <a
                routerLink="/account/listings"
                class="inline-flex shrink-0 items-center gap-1 rounded-full border border-line bg-cream/80 px-3 py-1.5 text-xs font-medium text-ink/70 transition hover:border-tan hover:text-ink"
              >
                <app-icon name="chevronLeft" svgClass="h-3.5 w-3.5" />
                Listings
              </a>

              <span class="h-8 w-px shrink-0 bg-line" aria-hidden="true"></span>

              <div class="flex min-w-0 items-center gap-3">
                <span
                  class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-creamdark text-tandark"
                >
                  <app-icon name="eye" svgClass="h-4 w-4" [strokeWidth]="2" />
                </span>
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <p class="font-display text-sm font-semibold text-ink">Seller preview</p>
                    <span
                      class="rounded-full px-2 py-0.5 text-[11px] font-medium"
                      [class]="productStatusBadgeClass(product.status)"
                    >
                      {{ productStatusLabel(product.status) }}
                    </span>
                  </div>
                  <p class="mt-0.5 text-xs leading-relaxed text-ink/55">
                    You’re viewing your listing as a buyer would — purchase actions stay disabled.
                  </p>
                </div>
              </div>
            </div>

            <div class="flex shrink-0 flex-col items-end gap-1.5">
              <div class="flex items-center gap-2">
                <a
                  [routerLink]="['/account/listings', product.id, 'edit']"
                  class="inline-flex items-center justify-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-sm font-medium text-ink transition hover:border-tan hover:bg-cream"
                >
                  <app-icon name="pencil" svgClass="h-3.5 w-3.5" [strokeWidth]="2" />
                  Edit
                </a>
                @if (product.status === 'DRAFT') {
                  <button
                    type="button"
                    class="inline-flex cursor-pointer items-center justify-center rounded-full bg-forest px-4 py-2 text-sm font-medium text-cream transition hover:bg-forestdark disabled:cursor-not-allowed disabled:opacity-60"
                    [disabled]="publishing()"
                    (click)="publishListing()"
                  >
                    {{ publishing() ? 'Publishing…' : 'Publish' }}
                  </button>
                }
              </div>
              @if (publishError()) {
                <p class="text-xs text-rose">{{ publishError() }}</p>
              }
            </div>
          </div>
        </div>
      </div>
    }

    <p class="text-sm text-ink/60">
      <a routerLink="/" class="hover:text-ink">Home</a> /
      <a routerLink="/shop" class="hover:text-ink">Shop</a> /
      <a
        routerLink="/shop"
        [queryParams]="{ discipline: product.discipline.slug }"
        class="hover:text-ink"
        >{{ product.discipline.name }}</a
      >
      /
      {{ product.title }}
    </p>

    <div class="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-2">
      <div>
        @if (product.images.length) {
          <app-snap-carousel
            [count]="product.images.length"
            [resetKey]="product.id"
            [showArrows]="true"
            ariaLabel="Product photos"
            dotLabel="Show photo"
            dotsClass="mt-3"
          >
            @for (image of product.images; track image.id) {
              <div class="w-full shrink-0 snap-start">
                <div class="aspect-[4/5] overflow-hidden rounded-2xl bg-creamdark">
                  <app-product-image
                    [src]="image.url"
                    [alt]="image.altText ?? product.title"
                    [priority]="$first"
                    [imgClass]="
                      isSold()
                        ? 'h-full w-full object-cover grayscale'
                        : 'h-full w-full object-cover'
                    "
                    label="No photo available"
                  />
                </div>
              </div>
            }
          </app-snap-carousel>
        } @else {
          <div class="aspect-[4/5] overflow-hidden rounded-2xl bg-creamdark">
            <app-product-image alt="" label="No photo available" />
          </div>
        }
      </div>

      <div>
        <div class="flex items-center gap-2">
          <p class="text-sm font-medium uppercase tracking-wide text-tandark">
            {{ product.discipline.name }}
          </p>
          @if (isSold()) {
            <span class="rounded-full bg-rose/90 px-2.5 py-1 text-xs font-medium text-cream"
              >Recently Sold</span
            >
          }
        </div>
        <div class="mt-1 flex items-start justify-between gap-4">
          <h1 class="font-display text-3xl font-semibold text-ink">{{ product.title }}</h1>
          <button
            type="button"
            class="cursor-pointer flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line disabled:cursor-not-allowed disabled:opacity-50"
            [class.text-rose]="isSaved()"
            [class.border-rose]="isSaved()"
            [disabled]="isOwner()"
            (click)="toggleSave()"
            aria-label="Save this saddle"
          >
            <svg
              viewBox="0 0 24 24"
              [attr.fill]="isSaved() ? 'currentColor' : 'none'"
              stroke="currentColor"
              stroke-width="1.5"
              class="h-5 w-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
              />
            </svg>
          </button>
        </div>

        <div class="mt-3 flex flex-wrap gap-2">
          <span class="rounded-full bg-creamdark px-3 py-1 text-xs font-medium text-ink">{{
            product.brand.name
          }}</span>
          <span class="rounded-full bg-creamdark px-3 py-1 text-xs font-medium text-ink"
            >Seat {{ product.seatSizeInches }}"</span
          >
          <span class="rounded-full bg-creamdark px-3 py-1 text-xs font-medium text-ink">{{
            formatCondition(product.condition)
          }}</span>
        </div>

        <p class="mt-6 font-display text-4xl font-semibold text-ink">
          {{ formatPriceCents(product.priceAmountCents, product.currency) }}
        </p>

        <div class="mt-3 flex flex-wrap gap-2 text-xs text-ink/60">
          @if (product.localPickupAvailable) {
            <span
              class="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1"
            >
              <app-icon name="mapPin" svgClass="h-3 w-3" [strokeWidth]="2" />
              Local pickup
            </span>
          }
          @if (product.deliveryAvailable) {
            <span
              class="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1"
            >
              <app-icon name="truck" svgClass="h-3 w-3" [strokeWidth]="2" />
              Delivery
            </span>
          }
        </div>

        @if (isSold()) {
          <div
            class="mt-5 flex items-start gap-3 rounded-xl bg-ink/5 px-4 py-3.5 ring-1 ring-ink/10"
          >
            <app-icon
              name="check"
              svgClass="mt-0.5 h-4 w-4 shrink-0 text-ink/60"
              [strokeWidth]="2"
            />
            <p class="text-sm font-medium text-ink">
              This saddle sold recently — buying, offers, and messaging are closed for this listing.
            </p>
          </div>
        } @else if (activeOrder(); as order) {
          <div
            class="mt-5 flex items-start gap-3 rounded-xl bg-forest/5 px-4 py-3.5 ring-1 ring-forest/15"
          >
            <app-icon
              name="clock"
              svgClass="mt-0.5 h-4 w-4 shrink-0 text-forest"
              [strokeWidth]="2"
            />
            <div class="min-w-0">
              <p class="text-sm font-medium text-ink">
                You already have {{ order.type === 'OFFER' ? 'an offer' : 'a request' }} in progress
                for this saddle
              </p>
              <a
                routerLink="/account/orders"
                class="mt-1 inline-flex items-center gap-1 text-sm font-medium text-forest hover:underline"
              >
                View in My Orders
                <app-icon name="chevronRight" svgClass="h-3.5 w-3.5" [strokeWidth]="2" />
              </a>
            </div>
          </div>
        }

        <div class="mt-5 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            class="cursor-pointer flex-1 rounded-full bg-forest px-6 py-3 text-sm font-medium text-cream transition hover:bg-forestdark disabled:cursor-not-allowed disabled:opacity-60"
            [disabled]="isOwner() || !!activeOrder() || isSold()"
            (click)="openCheckout('BUY_NOW')"
          >
            Buy Now
          </button>
          <button
            type="button"
            class="cursor-pointer flex-1 rounded-full border border-forest px-6 py-3 text-sm font-medium text-forest transition hover:bg-creamdark disabled:cursor-not-allowed disabled:opacity-60"
            [disabled]="isOwner() || !!activeOrder() || isSold()"
            (click)="openCheckout('OFFER')"
          >
            Make an Offer
          </button>
        </div>
        <button
          type="button"
          class="cursor-pointer mt-3 w-full rounded-full border border-line px-6 py-3 text-sm font-medium text-ink transition hover:bg-creamdark disabled:cursor-not-allowed disabled:opacity-60"
          [disabled]="isOwner() || isSold()"
          (click)="messageSeller()"
        >
          Message Seller
        </button>
        @if (isOwner() && !isSold()) {
          <p class="mt-2 text-center text-xs text-ink/50">
            You can't buy, offer on, or message about your own listing.
          </p>
        }

        @if (isSold()) {
          <button
            type="button"
            class="mt-3 w-full cursor-pointer rounded-full bg-rose px-6 py-3.5 text-base font-semibold text-cream transition hover:opacity-90"
            (click)="wantSomethingLikeThis()"
          >
            Find a saddle like this
          </button>
        }

        <div class="mt-6 rounded-xl bg-forest px-5 py-4 text-cream">
          <p class="font-display text-sm font-semibold">Buyer Protection</p>
          <p class="mt-1 text-xs text-tanlight">
            Funds are held securely until you confirm it's as described. Not right for your horse?
            Keep it and resell it on Galopa any time — free, 0% commission.
          </p>
        </div>

        <dl
          class="mt-6 grid grid-cols-2 gap-x-4 gap-y-4 rounded-2xl border border-line bg-white p-4 shadow-[0_1px_2px_rgba(33,31,26,0.04)] sm:p-5"
        >
          @for (spec of highlightSpecs(); track spec.label) {
            <div class="flex items-start gap-3">
              @if (spec.swatch) {
                <span
                  class="mt-0.5 h-8 w-8 shrink-0 rounded-full ring-1 ring-ink/10"
                  [style.backgroundColor]="spec.swatch"
                  [attr.title]="spec.value"
                ></span>
              } @else if (spec.icon) {
                <span
                  class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-creamdark text-tandark"
                >
                  <app-icon [name]="spec.icon" svgClass="h-4 w-4" [strokeWidth]="1.75" />
                </span>
              }
              <div class="min-w-0">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-ink/45">
                  {{ spec.label }}
                </dt>
                <dd class="mt-0.5 truncate text-sm font-medium text-ink">{{ spec.value }}</dd>
              </div>
            </div>
          }
        </dl>

        <div
          class="mt-6 flex items-center gap-3 rounded-2xl border border-line bg-white p-4 shadow-[0_1px_2px_rgba(33,31,26,0.04)]"
        >
          @if (product.seller.avatarUrl) {
            <img
              [src]="product.seller.avatarUrl"
              alt=""
              class="h-11 w-11 rounded-full object-cover"
            />
          } @else {
            <div
              class="flex h-11 w-11 items-center justify-center rounded-full bg-forest text-sm font-semibold text-cream"
            >
              {{ product.seller.firstName.charAt(0) }}
            </div>
          }
          <div class="flex-1">
            <p class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-medium text-ink">
              <span>{{ product.seller.firstName }} {{ product.seller.lastName.charAt(0) }}.</span>
              @if (product.seller.stripeChargesEnabled) {
                <span
                  class="inline-flex items-center gap-1 text-xs font-medium text-forest"
                  title="Verified seller"
                >
                  <app-icon name="shield" svgClass="h-3.5 w-3.5" [strokeWidth]="2" />
                  Verified
                </span>
              }
            </p>
            <div class="flex items-center gap-1.5 text-xs text-ink/60">
              <app-star-rating [rating]="product.seller.sellerRatingAvg" />
              <span>({{ product.seller.sellerRatingCount }} sales)</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
      <section
        class="overflow-hidden rounded-2xl border border-line bg-white shadow-[0_1px_2px_rgba(33,31,26,0.04)]"
      >
        <div class="flex items-center gap-3 border-b border-line px-5 py-4">
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-creamdark text-tandark"
          >
            <app-icon name="documentText" svgClass="h-4 w-4" [strokeWidth]="1.75" />
          </span>
          <h2 class="font-display text-lg font-semibold text-ink">Description</h2>
        </div>
        <div class="px-5 py-5">
          <p class="whitespace-pre-line text-sm leading-7 text-ink/75">
            {{ product.description }}
          </p>
        </div>
      </section>

      <section
        class="overflow-hidden rounded-2xl border border-line bg-white shadow-[0_1px_2px_rgba(33,31,26,0.04)]"
      >
        <div class="flex items-center gap-3 border-b border-line px-5 py-4">
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-creamdark text-tandark"
          >
            <app-icon name="queueList" svgClass="h-4 w-4" [strokeWidth]="1.75" />
          </span>
          <h2 class="font-display text-lg font-semibold text-ink">Full specifications</h2>
        </div>
        <dl class="divide-y divide-line px-5">
          @for (row of fullSpecRows(); track row.label) {
            <div class="flex items-center justify-between gap-4 py-3.5">
              <dt class="text-sm text-ink/50">{{ row.label }}</dt>
              <dd class="flex min-w-0 items-center gap-2 text-sm font-medium text-ink">
                @if (row.swatch) {
                  <span
                    class="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-ink/10"
                    [style.backgroundColor]="row.swatch"
                  ></span>
                }
                <span class="truncate">{{ row.value }}</span>
              </dd>
            </div>
          }
        </dl>
      </section>
    </div>

    @if (checkoutOpen()) {
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
        <div class="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6">
          <div class="flex items-start justify-between gap-4">
            <h2 class="font-display text-xl font-semibold text-ink">
              {{ checkoutType() === 'OFFER' ? 'Make an Offer' : 'Buy Now' }}
            </h2>
            <button
              type="button"
              class="cursor-pointer text-ink/50 hover:text-ink"
              (click)="closeCheckout()"
              aria-label="Close"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="h-5 w-5"
              >
                <path stroke-linecap="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p class="mt-1 text-sm text-ink/60">{{ product.title }}</p>

          @if (checkoutStep() === 'details') {
            <div class="mt-5 flex flex-col gap-4">
              @if (checkoutType() === 'OFFER') {
                <div>
                  <label class="block text-sm font-medium text-ink">Your offer (AUD)</label>
                  <input
                    type="text"
                    inputmode="decimal"
                    class="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm"
                    placeholder="e.g. 850"
                    [value]="offerAudInput()"
                    (input)="offerAudInput.set($any($event.target).value)"
                  />
                  <p class="mt-1 text-xs text-ink/50">
                    Listed at {{ formatPriceCents(product.priceAmountCents, product.currency) }}.
                  </p>
                </div>
              }

              @if (product.localPickupAvailable && product.deliveryAvailable) {
                <div>
                  <label class="block text-sm font-medium text-ink">How will you receive it?</label>
                  <div class="mt-2 flex gap-4">
                    <label class="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="radio"
                        name="deliveryMethod"
                        [checked]="deliveryMethod() === 'LOCAL_PICKUP'"
                        (change)="deliveryMethod.set('LOCAL_PICKUP')"
                      />
                      Local pickup
                    </label>
                    <label class="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="radio"
                        name="deliveryMethod"
                        [checked]="deliveryMethod() === 'DELIVERY'"
                        (change)="deliveryMethod.set('DELIVERY')"
                      />
                      Delivery
                    </label>
                  </div>
                </div>
              }

              @if (deliveryMethod() === 'DELIVERY') {
                @if (addresses().length) {
                  <div>
                    <label class="block text-sm font-medium text-ink">Delivery address</label>
                    <select
                      class="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm"
                      [value]="selectedAddressId()"
                      (change)="selectedAddressId.set($any($event.target).value)"
                    >
                      @for (a of addresses(); track a.id) {
                        <option [value]="a.id">{{ a.line1 }}, {{ a.suburb }} {{ a.state }}</option>
                      }
                    </select>
                  </div>
                } @else {
                  <p class="text-sm text-rose">
                    You don't have a saved address yet.
                    <a routerLink="/account/profile" class="font-medium underline">Add one</a> then
                    come back.
                  </p>
                }
              }

              <div class="rounded-lg bg-creamdark p-3 text-xs text-ink/70">
                A service fee applies to every purchase, shown in the summary below. Choosing
                delivery adds 1% shipping insurance plus a delivery fee based on a live Australia
                Post quote. Your card is only <strong>authorized</strong> now — it's charged only
                once the seller accepts, which they have 48 hours to do.
              </div>

              @if (checkoutError()) {
                <p class="text-sm text-rose">{{ checkoutError() }}</p>
              }

              <button
                type="button"
                class="cursor-pointer w-full rounded-full bg-forest px-6 py-3 text-sm font-medium text-cream transition hover:bg-forestdark disabled:opacity-60"
                [disabled]="!canProceedToPayment() || submitting()"
                (click)="proceedToPayment()"
              >
                {{ submitting() ? 'Preparing…' : 'Continue to payment' }}
              </button>
            </div>
          } @else if (quote(); as quote) {
            <div class="mt-5 flex flex-col gap-4">
              <dl class="flex flex-col gap-1.5 rounded-lg bg-creamdark p-3 text-sm">
                <div class="flex justify-between">
                  <dt class="text-ink/60">
                    {{ checkoutType() === 'OFFER' ? 'Your offer' : 'Item price' }}
                  </dt>
                  <dd class="text-ink">
                    {{ formatPriceCents(quote.amountCents, quote.currency) }}
                  </dd>
                </div>
                <div class="flex justify-between">
                  <dt class="text-ink/60">Service fee ({{ quote.serviceFeeRateBps / 100 }}%)</dt>
                  <dd class="text-ink">
                    {{ formatPriceCents(quote.serviceFeeCents, quote.currency) }}
                  </dd>
                </div>
                @if (quote.shippingInsuranceCents) {
                  <div class="flex justify-between">
                    <dt class="text-ink/60">Shipping insurance (1%)</dt>
                    <dd class="text-ink">
                      {{ formatPriceCents(quote.shippingInsuranceCents, quote.currency) }}
                    </dd>
                  </div>
                }
                @if (quote.deliveryFeeCents) {
                  <div class="flex justify-between">
                    <dt class="text-ink/60">Delivery fee</dt>
                    <dd class="text-ink">
                      {{ formatPriceCents(quote.deliveryFeeCents, quote.currency) }}
                    </dd>
                  </div>
                }
                <div class="flex justify-between border-t border-line pt-1.5 font-medium">
                  <dt class="text-ink">Total to authorize</dt>
                  <dd class="text-ink">
                    {{ formatPriceCents(quote.totalAmountCents, quote.currency) }}
                  </dd>
                </div>
              </dl>

              <div #paymentElementContainer></div>

              @if (checkoutError()) {
                <p class="text-sm text-rose">{{ checkoutError() }}</p>
              }

              <div class="flex gap-3">
                <button
                  type="button"
                  class="cursor-pointer flex-1 rounded-full bg-forest px-6 py-3 text-sm font-medium text-cream transition hover:bg-forestdark disabled:opacity-60"
                  [disabled]="submitting()"
                  (click)="submitPayment()"
                >
                  {{
                    submitting()
                      ? 'Processing…'
                      : checkoutType() === 'OFFER'
                        ? 'Submit offer'
                        : 'Confirm purchase'
                  }}
                </button>
                <button
                  type="button"
                  class="cursor-pointer rounded-full border border-line px-4 py-3 text-sm font-medium text-ink hover:bg-creamdark"
                  [disabled]="submitting()"
                  (click)="backToDetails()"
                >
                  Back
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    }
  </div>
} @else if (loadError()) {
  <div class="mx-auto max-w-2xl px-4 py-24 text-center">
    <p class="text-ink/60">This listing isn't available.</p>
    <a routerLink="/shop" class="mt-4 inline-block font-medium text-forest underline"
      >Browse listings</a
    >
  </div>
} @else {
  <div class="mx-auto max-w-2xl px-4 py-24 text-center">
    <p class="text-ink/60">Loading listing…</p>
  </div>
}
